import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ProductCard from "../product/ProductCard";
import SkeletonGrid from "../common/SkeletonGrid";
import { API_BASE as API } from "../../api/api";
import { getSessionId } from "../../utils/analytics";
import { useSEO } from "../../utils/seo";
import "./RailPage.css";

const PAGE_SIZE = 24;

const SORTS = [
  { value: "score",  label: "Recommended" },
  { value: "newest",  label: "Newest" },
  { value: "price",   label: "Price: Low to High" },
];

const RailPage = ({ onAddToCart }) => {
  const { slug } = useParams();
  const [displayName, setDisplayName] = useState("");
  const [sort, setSort] = useState("score");
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useSEO({
    title: displayName || "Collection",
    description: displayName ? `Browse ${displayName} on Emporia, a Kenyan online marketplace with secure M-Pesa payments and nationwide delivery.` : "Browse this collection at Emporia.",
    path: `/rails/${slug}`,
  });

  // Rail metadata (display name) — fetched once per slug, not re-fetched on sort/page change.
  useEffect(() => {
    setNotFound(false);
    fetch(`${API}/rails`)
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then((rails) => {
        const rail = rails.find((r) => r.slug === slug);
        if (rail) setDisplayName(rail.display_name);
        else setNotFound(true);
      })
      .catch(() => {});
  }, [slug]);

  useEffect(() => {
    setPage(1);
  }, [slug, sort]);

  useEffect(() => {
    if (notFound) return;
    setLoading(true);
    const sessionId = getSessionId();
    const url = `${API}/rails/${slug}/page?sort=${sort}&page=${page}&page_size=${PAGE_SIZE}&session_id=${encodeURIComponent(sessionId)}`;
    fetch(url)
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then((data) => {
        setItems(data.items || []);
        setTotal(data.total || 0);
        setLoading(false);
      })
      .catch(() => {
        setItems([]);
        setTotal(0);
        setLoading(false);
      });
  }, [slug, sort, page, notFound]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  if (notFound) {
    return (
      <main className="rail-page">
        <div className="rail-empty">
          <p>This collection doesn't exist.</p>
          <p>Browse our <Link to="/">homepage</Link> instead.</p>
        </div>
      </main>
    );
  }

  return (
    <main className="rail-page">
      <div className="rail-header">
        <h1>{displayName || " "}</h1>
        {!loading && (
          <p className="rail-count">
            {total === 0 ? "No products found" : `${total} product${total !== 1 ? "s" : ""}`}
          </p>
        )}
        <select
          className="rail-sort"
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          aria-label="Sort"
        >
          {SORTS.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <SkeletonGrid count={4} className="rail-grid" />
      ) : items.length > 0 ? (
        <>
          <div className="rail-grid">
            {items.map((p, i) => (
              <div key={p.id} className="product-card-anim" style={{ "--i": i }}>
                <ProductCard product={p} onAddToCart={onAddToCart} />
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="rail-pagination">
              <button
                className="rail-page-btn"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                ← Previous
              </button>
              <span className="rail-page-status">Page {page} of {totalPages}</span>
              <button
                className="rail-page-btn"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next →
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="rail-empty">
          <p>No products currently match this collection.</p>
          <p>Browse our <Link to="/">homepage</Link> instead.</p>
        </div>
      )}
    </main>
  );
};

export default RailPage;
